import {
  ActionIcon,
  Group,
  NumberInput,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { useParams } from "@tanstack/react-router";
import {
  Focus,
  Info,
  LaptopMinimal,
  Scaling,
  Settings2,
  SunMedium,
  Waves,
} from "lucide-react";
import { useState, type ReactNode } from "react";

import type {
  CatalogVideoModel,
  SettingRange,
  UpscaleCaps,
} from "../../../api/generated/model";
import {
  VideoReframeQuote,
  VideoReframeRequest,
  VideoUpscaleQuote,
  VideoUpscaleRequest,
} from "../../../api/generated/zod";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import { Choice } from "../../../ui/Choice";
import { GlassPanel } from "../../../ui/GlassPanel";
import { ModelPicker } from "../../../ui/ModelPicker";
import { RatioGlyph } from "../../../ui/RatioGlyph";
import {
  Errors,
  ROUTE,
  StartGroup,
  VideoSlot,
  defaultModel,
  rowsOf,
  type ToolFormProps,
} from "./form-parts";
import { RangeChip, onTyped } from "./RangeChip";
import classes from "./tools.module.css";
import { useToolVideo, type ToolVideo } from "./use-tool-video";

type UpscaleKey = keyof UpscaleCaps;

/** Live's four footer sliders, their words, icons, tips and decimals (`video-upscale-form`). */
const SLIDERS: { key: UpscaleKey; label: string; icon: ReactNode; tip: string; decimals: number }[] = [
  {
    key: "upscale_factor",
    label: "Upscale",
    icon: <Scaling size={20} strokeWidth={1.5} />,
    tip: "Factor to upscale the video by (e.g. 2.0 doubles width and height)",
    decimals: 1,
  },
  {
    key: "noise",
    label: "Noise",
    icon: <Waves size={20} strokeWidth={1.5} />,
    tip: "Noise reduction level (0.0-1.0)",
    decimals: 2,
  },
  {
    key: "halo",
    label: "Halo",
    icon: <SunMedium size={20} strokeWidth={1.5} />,
    tip: "Halo reduction level (0.0-1.0)",
    decimals: 2,
  },
  {
    key: "recover_detail",
    label: "Recovery",
    icon: <Focus size={20} strokeWidth={1.5} />,
    tip: "Recover original detail level (0.0-1.0). Higher values preserve more original detail.",
    decimals: 2,
  },
];

/** Live's three advanced settings, in its order (`video-upscale-form/side-panel`). */
const ADVANCED: { key: UpscaleKey; label: string; tip: string; decimals: number }[] = [
  { key: "grain", label: "Grain", tip: "Film grain amount (0.0-0.1).", decimals: 2 },
  {
    key: "target_fps",
    label: "Target FPS",
    tip: "Target FPS for frame interpolation. If set, frame interpolation will be enabled.",
    decimals: 0,
  },
  {
    key: "compression",
    label: "Compression",
    tip: "Compression artifact removal level (0.0-1.0).",
    decimals: 2,
  },
];

type UpscaleValues = Partial<Record<UpscaleKey, number | null>>;

/** A model's starting values: each range's default, where it has one. */
function upscaleDefaults(caps: UpscaleCaps | null | undefined): UpscaleValues {
  const values: UpscaleValues = {};
  for (const [key, range] of Object.entries(caps ?? {}) as [UpscaleKey, SettingRange | null][]) {
    values[key] = range?.default ?? null;
  }
  return values;
}

/**
 * Live's Video Upscale form (#211, `video-upscale-form`, captures 08 to 08c, G7-video-tools):
 * the "Video" slot and no prompt; the model; live's four sliders (Upscale, Noise, Halo,
 * Recovery) each in its popover; the advanced settings beside the panel (Grain, Target FPS,
 * Compression); the price and the start. Only the values set are sent; another model starts at
 * its own defaults, as live's form refills them.
 */
export function UpscaleForm({ catalog, above, overlay, onStarted, inline }: ToolFormProps) {
  const { teamId, brandId } = useParams({ from: ROUTE });
  const models = rowsOf(catalog, "video.upscale");
  const [modelId, setModelId] = useState(() => defaultModel(models).id);
  const model = models.find((each) => each.id === modelId) ?? defaultModel(models);
  const caps = model.modes["video.upscale"]?.upscale;
  const [values, setValues] = useState<UpscaleValues>(() => upscaleDefaults(caps));
  const [collapsed, setCollapsed] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const tool = useToolVideo(teamId, brandId);
  const set = (key: UpscaleKey, value: number | null) =>
    setValues((current) => ({ ...current, [key]: value }));
  const fields = {
    type: "video.upscale" as const,
    model: model.id,
    ...(tool.ref === null ? {} : { source_video: tool.ref }),
    ...Object.fromEntries(Object.entries(values).filter(([, value]) => value !== null)),
  };
  const quote = VideoUpscaleQuote.safeParse(fields);
  const request = VideoUpscaleRequest.safeParse(fields);
  const job = usePricedStart({
    teamId,
    brandId,
    quote: quote.success ? quote.data : undefined,
    request: request.success ? request.data : undefined,
    onStarted,
  });
  const advanced = ADVANCED.filter((each) => caps?.[each.key] != null);
  return (
    <GlassPanel
      inline={inline}
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      header={<VideoSlot tool={tool} />}
      summary={<span>Video Upscale</span>}
      above={above}
      overlay={overlay}
      aside={
        advancedOpen && advanced.length > 0 ? (
          <Stack gap="sm" aria-label="Advanced settings">
            {advanced.map((each) => {
              const range = caps![each.key]!;
              return (
                <Group key={each.key} justify="space-between" wrap="nowrap">
                  <Group gap={4} wrap="nowrap">
                    <Text size="sm">{each.label}</Text>
                    <Tooltip label={each.tip} withArrow multiline maw={300}>
                      <Info size={14} aria-hidden />
                    </Tooltip>
                  </Group>
                  <NumberInput
                    className={classes.advanced}
                    aria-label={each.label}
                    min={range.min}
                    max={range.max}
                    step={range.step}
                    decimalScale={each.decimals}
                    allowDecimal={each.decimals > 0}
                    allowNegative={false}
                    clampBehavior="strict"
                    value={values[each.key] ?? ""}
                    onChange={onTyped((next) => set(each.key, next))}
                  />
                </Group>
              );
            })}
          </Stack>
        ) : undefined
      }
      footer={
        <Group justify="space-between" wrap="nowrap" gap={12}>
          <Group gap={8} wrap="nowrap">
            <ModelPicker
              models={models}
              value={model.id}
              onChange={(id) => {
                const next = models.find((each) => each.id === id);
                if (next === undefined) return;
                setModelId(id);
                setValues(upscaleDefaults(next.modes["video.upscale"]?.upscale));
              }}
            />
            {SLIDERS.filter((each) => caps?.[each.key] != null).map((each) => (
              <RangeChip
                key={each.key}
                label={each.label}
                icon={each.icon}
                tip={each.tip}
                range={caps![each.key]!}
                value={values[each.key] ?? null}
                decimals={each.decimals}
                onChange={(value) => set(each.key, value)}
              />
            ))}
            <Tooltip label="Advanced settings" withArrow>
              <ActionIcon
                variant={advancedOpen ? "light" : "subtle"}
                radius={8}
                aria-label="Advanced settings"
                aria-expanded={advancedOpen}
                disabled={advanced.length === 0}
                onClick={() => setAdvancedOpen((open) => !open)}
              >
                <Settings2 size={20} strokeWidth={1.5} />
              </ActionIcon>
            </Tooltip>
          </Group>
          <StartGroup job={job} notes={model.modes["video.upscale"]?.notes ?? []} />
        </Group>
      }
    >
      <Errors job={job} uploads={[tool.upload]} />
      {tool.dialog}
    </GlassPanel>
  );
}

/** Live's preferred ratio for a reframe (`video-reframe-form/constants.ts`). */
const PREFERRED_RATIO = "9:16";

/** A ratio's width over height, "16:9" as 1.78. */
function ratioValue(ratio: string): number | null {
  const [width, height] = ratio.split(":").map(Number);
  return width && height ? width / height : null;
}

/**
 * The ratios a reframe of this video offers: the model's, less the video's own (within 0.01 of
 * its width over height, live's rule), when its size is known.
 */
function ratiosFor(model: CatalogVideoModel, tool: ToolVideo): string[] {
  const { width, height } = tool.video ?? {};
  const own = width && height ? width / height : null;
  return model.aspect_ratios.values.filter((ratio) => {
    const value = ratioValue(ratio);
    return own === null || value === null || Math.abs(value - own) >= 0.01;
  });
}

/** Live's default: 9:16 where offered, else the model's default, else the first. */
function ratioDefault(model: CatalogVideoModel, offered: string[]): string | null {
  if (offered.includes(PREFERRED_RATIO)) return PREFERRED_RATIO;
  if (offered.includes(model.aspect_ratios.default)) return model.aspect_ratios.default;
  return offered[0] ?? null;
}

/**
 * Live's Reframe video form (#212, `video-reframe-form`, capture 13, G7-video-tools): the
 * "Video" slot and no prompt; the model; the ratio, live's 9:16 first, never the video's own
 * (live greys it; ours leaves it out); the resolution, live's 1080p; the price and the start.
 */
export function ReframeForm({ catalog, above, overlay, onStarted, inline }: ToolFormProps) {
  const { teamId, brandId } = useParams({ from: ROUTE });
  const models = rowsOf(catalog, "video.reframe");
  const [modelId, setModelId] = useState(() => defaultModel(models).id);
  const model = models.find((each) => each.id === modelId) ?? defaultModel(models);
  const [collapsed, setCollapsed] = useState(false);
  const tool = useToolVideo(teamId, brandId);
  const offered = ratiosFor(model, tool);
  const [kept, setRatio] = useState<string | null>(null);
  // The ratio shown while the model and the video still offer it, else live's default. A move to
  // the default is kept too, so a later video that offers both stays on it, as live's form does.
  const ratio = kept !== null && offered.includes(kept) ? kept : ratioDefault(model, offered);
  if (ratio !== kept) setRatio(ratio);
  const resolutions = model.resolutions?.values ?? [];
  const [chosenResolution, setResolution] = useState<string | null>(null);
  const resolution =
    chosenResolution !== null && resolutions.includes(chosenResolution)
      ? chosenResolution
      : (model.resolutions?.default ?? null);
  const fields = {
    type: "video.reframe" as const,
    model: model.id,
    ...(tool.ref === null ? {} : { source_video: tool.ref }),
    ...(ratio === null ? {} : { aspect_ratio: ratio }),
    ...(resolution === null ? {} : { resolution }),
  };
  const quote = VideoReframeQuote.safeParse(fields);
  const request = VideoReframeRequest.safeParse(fields);
  const job = usePricedStart({
    teamId,
    brandId,
    quote: quote.success ? quote.data : undefined,
    request: request.success ? request.data : undefined,
    onStarted,
  });
  return (
    <GlassPanel
      inline={inline}
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      header={<VideoSlot tool={tool} />}
      summary={<span>Reframe video</span>}
      above={above}
      overlay={overlay}
      footer={
        <Group justify="space-between" wrap="nowrap" gap={12}>
          <Group gap={8} wrap="nowrap">
            <ModelPicker models={models} value={model.id} onChange={setModelId} />
            {ratio === null ? null : (
              <Choice
                setting="Aspect ratio"
                icon={<RatioGlyph ratio={ratio} />}
                chevron={false}
                width={70}
                tip="Aspect ratio"
                value={ratio}
                options={offered.map((each) => ({ value: each, label: each }))}
                onChange={setRatio}
              />
            )}
            {resolution === null ? null : (
              <Choice
                setting="Resolution"
                icon={<LaptopMinimal size={20} strokeWidth={1.5} />}
                chevron={false}
                width={80}
                tip="Resolution"
                value={resolution}
                options={resolutions.map((each) => ({ value: each, label: each }))}
                onChange={setResolution}
              />
            )}
          </Group>
          <StartGroup job={job} notes={model.modes["video.reframe"]?.notes ?? []} />
        </Group>
      }
    >
      <Errors job={job} uploads={[tool.upload]} />
      {tool.dialog}
    </GlassPanel>
  );
}
